import dedent from 'ts-dedent';
import { v4 as uuid4 } from 'uuid';
import { ColumnDataType, Provider, WorkflowCodeType, WorkflowComponentParamType, WorkflowComponentParameterMode, WorkflowParameterValueErrorType } from '../types';
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent';
class ExtractRaster extends SingleOutputWorkflowComponent {
    constructor() {
        super({
            name: 'carto.extractraster',
            group: 'Raster Operations',
            title: 'Get Values from Raster',
            description: `
        This component allows obtaining values from a raster table. The values included in the output can be determined by a vector data source input.
      `,
            starred: true
        }, [
            {
                name: 'source',
                type: WorkflowComponentParamType.InputTable,
                title: 'Raster table'
            },
            {
                name: 'vector',
                type: WorkflowComponentParamType.InputTable,
                title: 'Vector table',
                optional: true
            },
            {
                name: 'geo',
                type: WorkflowComponentParamType.Column,
                title: 'Geo column',
                parent: 'vector',
                dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
            },
            {
                name: 'columns',
                type: WorkflowComponentParamType.Column,
                title: 'Raster properties',
                parent: 'source',
                dataType: [ColumnDataType.Binary],
                mode: WorkflowComponentParameterMode.Multiple,
                optional: true
            },
            {
                name: 'addcustomexpression',
                type: WorkflowComponentParamType.Boolean,
                title: 'Add custom expression',
                optional: true,
                default: false
            },
            {
                name: 'expression',
                type: WorkflowComponentParamType.StringSql,
                title: '',
                optional: true,
                showIf: [{ parameter: 'addcustomexpression', value: true }],
                placeholder: 'band_1 + band_2 AS band_sum'
            },
            {
                name: 'addpixelcolumn',
                type: WorkflowComponentParamType.Boolean,
                title: 'Add pixel column',
                optional: true,
                default: false,
                advanced: true
            }
        ]);
        this.supportedProviders = [Provider.BigQuery, Provider.Snowflake];
    }
    async getCode({ workflow, params, connection, envVars, codeType, schemas }) {
        const dryRun = codeType === WorkflowCodeType.DryRun;
        let outputTablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'result', codeType);
        const prepParams = this._prepareParameters(params);
        let expression = (prepParams.columns || []).join(', ');
        if (prepParams.expression && expression) {
            expression = `${expression},${prepParams.expression}`;
        }
        else if (prepParams.expression) {
            expression = prepParams.expression;
        }
        expression = expression.replace(/\n/g, '');
        const dryRunExpression = expression
            .split(',')
            .map((x) => x.split(' ').pop())
            .map((x) => `1 AS ${x}`)
            .join(', ');
        const tempOutputTable = `${connection.tempStoragePath}.temp${uuid4().replace(/-/g, '_')}`;
        const removePixelColumn = !prepParams.addpixelcolumn;
        const storedProc = codeType === WorkflowCodeType.StoredProcedure;
        let query = '';
        const options = `{"include_pixel": ${JSON.stringify(prepParams.addpixelcolumn)}}`;
        if (!prepParams.vector) {
            if (dryRun) {
                const select = `SELECT
          ${dryRunExpression}${removePixelColumn ? '' : ', 1 AS pixel'}
          FROM ${connection.quoteName(prepParams.source)}`;
                query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null);
            }
            else {
                query = dedent `
          DROP TABLE IF EXISTS ${connection.quoteName(storedProc ? tempOutputTable : outputTablename)};
          CALL ${connection.quoteName(envVars.analyticsToolboxDataset)}.RASTER_VALUES(
              '${prepParams.source}',
              NULL,
              '${expression}',
              '${storedProc ? tempOutputTable : outputTablename}',
              '${options}'
          );
          ${storedProc
                    ? dedent `
                CREATE TEMP TABLE ${connection.quoteName(outputTablename)} AS
                  SELECT *
                  FROM ${connection.quoteName(tempOutputTable)};
                  DROP TABLE ${connection.quoteName(tempOutputTable)};`
                    : ''}
          `;
            }
        }
        else {
            switch (connection.type) {
                case Provider.BigQuery: {
                    let [project, dataset, tablename] = prepParams.vector.split('.');
                    const datasetname = storedProc
                        ? connection.tempStoragePath
                        : `${project}.${dataset}`;
                    tablename = storedProc
                        ? `table_${uuid4().replace(/-/g, '_')}`
                        : tablename;
                    const storedProcTable = connection.quoteName(`${connection.tempStoragePath}.${tablename}`);
                    query = dedent `
            BEGIN
            DECLARE nonGeomCols STRING;
            ${!dryRun ? 'DECLARE inputQuery STRING;' : ''}
            ${storedProc
                        ? dedent `
                    CREATE TABLE ${storedProcTable} AS
                    SELECT * FROM ${connection.quoteName(prepParams.vector)}
                    WHERE 1=0;`
                        : ''}
            EXECUTE IMMEDIATE
            '''
            with __cols AS(
                SELECT column_name
                FROM \`${datasetname}\`.INFORMATION_SCHEMA.COLUMNS
                WHERE table_name = '${tablename}' AND data_type != 'GEOGRAPHY'
            )
            SELECT STRING_AGG(column_name, ', ')
            FROM __cols
            '''
            INTO nonGeomCols;
            ${storedProc ? dedent `DROP TABLE ${storedProcTable};` : ''}
            ${dryRun
                        ? dedent `
                    EXECUTE IMMEDIATE
                    REPLACE(
                    '''
                      ${connection.getCreateTempTableQuery(outputTablename, `SELECT
                          ${removePixelColumn ? '' : '1 AS pixel,'}
                          ${dryRunExpression},
                          %nonGeomCols%,
                        FROM
                          ${connection.quoteName(prepParams.source)},
                          ${connection.quoteName(prepParams.vector)}`, codeType, null)}
                    ''',
                    '%nonGeomCols%',
                    nonGeomCols
                    );
                    `
                        : dedent `
                    SET inputQuery =
                      CONCAT(
                          'SELECT ${prepParams.geo} AS geom, ',
                          nonGeomCols,
                          ' FROM ${connection.quoteName(prepParams.vector)}'
                      );
                    DROP TABLE IF EXISTS ${connection.quoteName(storedProc ? tempOutputTable : outputTablename)};
                    CALL ${connection.quoteName(envVars.analyticsToolboxDataset)}.RASTER_VALUES(
                        '${prepParams.source}',
                        inputQuery,
                        '${expression}',
                        '${storedProc ? tempOutputTable : outputTablename}',
                        '${options}'
                    );`}
            ${storedProc
                        ? dedent `
                  CREATE TEMP TABLE ${outputTablename} AS
                    SELECT *
                    FROM ${connection.quoteName(tempOutputTable)};
                  DROP TABLE ${connection.quoteName(tempOutputTable)};
                  `
                        : ''}
            END;
            `;
                    break;
                }
                case Provider.Snowflake: {
                    const [datasetname, schemaname, tablename] = prepParams.vector.split('.');
                    query = dedent `
            DECLARE
              nonGeomCols STRING;
              inputQuery STRING;
            BEGIN
              LET res resultset := (EXECUTE IMMEDIATE '
                with __cols AS(
                  SELECT column_name
                  FROM ${datasetname}.INFORMATION_SCHEMA.COLUMNS
                  WHERE table_name = ''${tablename.toUpperCase()}'' AND
                    table_schema = ''${schemaname.toUpperCase()}'' AND
                    table_catalog = ''${datasetname.toUpperCase()}'' AND
                    data_type != ''GEOGRAPHY''
                )
                SELECT LISTAGG(column_name, '', '') cols
                FROM __cols
              ');
              LET cur_res CURSOR FOR res;
              OPEN cur_res;
              FETCH cur_res INTO nonGeomCols;
              SET inputQuery :=
                CONCAT(
                    'SELECT ${prepParams.geo} AS geom, ',
                    :nonGeomCols,
                    ' FROM ${connection.quoteName(prepParams.vector)}'
                );
              ${dryRun
                        ? dedent `
                    EXECUTE IMMEDIATE
                    REPLACE(
                    '
                      ${connection.getCreateTempTableQuery(outputTablename, `SELECT
                          ${removePixelColumn ? '' : '1 AS pixel,'}
                          ${dryRunExpression},
                          %nonGeomCols%,
                        FROM
                          ${connection.quoteName(prepParams.source)},
                          ${connection.quoteName(prepParams.vector)}`, codeType, null)}
                    ',
                    '%nonGeomCols%',
                    :nonGeomCols
                    );
                    `
                        : dedent `
                      DROP TABLE IF EXISTS ${connection.quoteName(outputTablename)};
                      CALL ${connection.quoteName(envVars.analyticsToolboxDataset)}.RASTER_VALUES(
                          '${prepParams.source}',
                          :inputQuery,
                          '${expression}',
                          '${outputTablename}',
                          '${options}'
                      );`}
              END;
              `;
                    break;
                }
            }
        }
        query = connection.wrapCodeInIfTableExistsBlock(query, [outputTablename], codeType);
        return {
            query,
            outputs: { result: outputTablename }
        };
    }
    checkInputValues({ params, resolvedParamValues, connection, dryRun, schemas, variables }) {
        const problems = super.checkInputValues({
            params,
            resolvedParamValues,
            connection,
            dryRun,
            schemas,
            variables
        });
        if (resolvedParamValues.columns == null &&
            resolvedParamValues.expression == null) {
            problems.push({
                parameter: 'expression',
                type: WorkflowParameterValueErrorType.MissingValue,
                message: 'At least one of "expression" and "columns" parameters must be specified'
            });
        }
        return problems;
    }
    requiredAnalyticsToolboxVersion(provider) {
        switch (provider) {
            case Provider.Snowflake:
                return '1.0.0';
            default:
                return null;
        }
    }
}
export { ExtractRaster };
